/**
 * Module that contains utils for text and html
 */
module.exports = {
    linkDataAtribute,
    tagAttributes,
    tagAttributesObject,
    buildTags,
    getConfigPlaceholder,
    setConfigPlaceholder,
    setSpacingPlaceholder,
    cutStringByWords,
    cutParagraphsByWords,
    countStringWords
};

/**
 * Pug syntax &attributes -> https://pugjs.org/language/attributes.html
 * Build string of the next data structure from an array
 *
 * @param {Array} [dataAttributes=[]]
 * @returns {object}
 */
function linkDataAtribute(dataAttributes = []) {
    const data = {};
    dataAttributes.forEach(item => {
        data[`data-${item.name}`] = item.value;
    });
    return data;
}

/**
 * Return html tags attributes as a string
 *
 * @param {array} attributesArray array with a data of the tag attributes
 * @returns {string} the string of HTML attrs
 */
function tagAttributes(attributesArray) {
    const attributesObject = tagAttributesObject(attributesArray);
    return Object.keys(attributesObject)
        .map(name => `${name}= "${attributesObject[name]}"`)
        .join(' ');
}

/**
 * Return html tags attributes as an object
 *
 * @param {array} attributesArray array with a data of the tag attributes
 * @returns {object} the object of HTML attrs
 */
function tagAttributesObject(attributesArray) {
    const attributesObject = {};

    attributesArray
        .filter(item => !!item.name)
        .forEach(item => {
            // if value is an array, just join the values as a single string
            const value = Array.isArray(item.value) ? item.value.join(' ') : item.value;
            attributesObject[item.name] = value;
        });

    return attributesObject;
}

/**
 * Builds a list of tags
 *
 * @param {Array} tagsData The array of data for each tag. Each one { content, content_tag?, attributes? }
 * @param {string} [tag='span'] The tag name (span by default)
 * @returns markup html tag with your attributes if has it
 */
function buildTags(tagsData, tag = 'span') {
    return tagsData
        .map(item => {
            const attributes = item.attributes ? ` ${tagAttributes(item.attributes)}` : '';
            let html;
            if (item.content_tag) {
                html = `<${tag}${attributes}><${item.content_tag}>${item.content}</${item.content_tag}></${tag}>`;
            } else {
                html = `<${tag}${attributes}>${item.content}</${tag}>`;
            }
            return html;
        })
        .join('');
}

/**
 * Generates config for placeholders
 *
 * @param {any} config Model.{placeholder}.data.config
 * @returns config object
 */
function getConfigPlaceholder(config) {
    const newConfig = {};
    newConfig.vertical_spacing_top = config.vertical_spacing_top || false;
    newConfig.vertical_spacing_bottom = config.vertical_spacing_bottom || false;
    newConfig.class = config.class || false;
    return newConfig;
}

/**
 * Sets placeholders layout
 * @param {object} config - Model.{placeholder}.data.config
 * @returns {string} return class names
 */
function setConfigPlaceholder(config) {
    const newConfig = getConfigPlaceholder(config);
    const spacingClass = setSpacingPlaceholder(newConfig.vertical_spacing_top, newConfig.vertical_spacing_bottom);
    let classes;

    if (!spacingClass && !newConfig.class) {
        classes = false;
    } else if (spacingClass) {
        classes = `${spacingClass} `;
    } else {
        classes = '';
    }

    if (newConfig.class) {
        newConfig.class.forEach(item => {
            classes += `${item} `;
        });
    }

    return classes;
}

/**
 * Sets class name for the vertical spacing of layout containers("placeholder")
 *
 * @param {any} top Model.{placeholder}.config.vertical_spacing_top
 * @param {any} bottom Model.{placeholder}.config.vertical_spacing_bottom
 * @returns {string} class name
 */
function setSpacingPlaceholder(top, bottom) {
    const verticalSpacingTop = top ? 'spacing-top' : false;
    const verticalSpacingBottom = bottom ? 'spacing-bottom' : false;
    let spacing = false;

    if (verticalSpacingBottom && verticalSpacingTop) {
        spacing = `${verticalSpacingTop} ${verticalSpacingBottom}`;
        return spacing;
    }

    if (verticalSpacingTop) {
        spacing = verticalSpacingTop;
    }

    if (verticalSpacingBottom) {
        spacing = verticalSpacingBottom;
    }

    return spacing;
}

/**
 * Cut long string by words ( or not ) how you want
 *
 * @param {any} text text to be cutted
 * @param {any} [{ limit = 155, splitWord = false, ellipsis = '...' }={}] Options:
 *              - limit:  maximum of characters to be displayed, NOT WORDS
 *              - splitWord: cut words splitting in the middle. if true, will get the last entire words and cut there.
 *              - ellipsis: custom ellipsis for the end of the string
 * @returns {string}
 * @example cutStringByWords('my string', { limit: 40} )
 * @example cutStringByWords('my string', { limit: 40, splitWord: true })
 * @example cutStringByWords('my string', { limit: 40, splitWord: true, elipsis: 'xxx' })
 */
function cutStringByWords(text, { limit = 155, splitWord = false, ellipsis = '...' } = {}) {
    const textWithSpace = `${text} `;
    let trimmedString = textWithSpace.substr(0, limit || 155);
    if (!splitWord) {
        trimmedString = trimmedString.substr(0, Math.min(trimmedString.length, trimmedString.lastIndexOf(' ')));
    }
    if (trimmedString.length + 1 < textWithSpace.length && ellipsis !== ' ') {
        trimmedString += ellipsis;
    }
    return trimmedString;
}

/**
 * Cuts a list of paragraphs using a character limit
 *
 * @param {Array} paragraphs An array of paragraphs
 * @param {Number} limit The character limit
 * @param {string} [ellipsis='...']
 * @returns {Array} The array of paragraphs
 */
function cutParagraphsByWords(paragraphs, limit, ellipsis = '...') {
    const paragraphsSize = paragraphs.length;
    const paragraphsProcessed = [];
    let wordsTotal = 0;

    for (let i = 0; i < paragraphsSize; i++) {
        let words = paragraphs[i].split(' ');

        if (wordsTotal + words.length <= limit) {
            wordsTotal += words.length;
            paragraphsProcessed.push(paragraphs[i]);
        } else {
            const elementsToInclude = limit - wordsTotal;
            if (elementsToInclude > 0) {
                words = words.splice(0, elementsToInclude);
                paragraphsProcessed.push(words.join(' ') + ellipsis);
            } else {
                paragraphsProcessed[i - 1] = paragraphsProcessed[i - 1] + ellipsis;
            }
            i = paragraphsSize + 1;
        }
    }

    return closeMardownInParagraphs(paragraphsProcessed, ellipsis);
}

function closeMardownInParagraphs(paragraphs, ellipsis = '...') {
    const hasStrongTags = text => text.match(/(\*\*)/g) && text.match(/(\*\*)/g).length % 2 !== 0;

    return paragraphs.map(p => {
        if (hasStrongTags(p)) {
            return p.replace(ellipsis, `**${ellipsis}`);
        }
        return p;
    });
}

/**
 * @name countStringWords
 * @summary count the words from a string
 * @param {string} text - text to be counted
 * @returns {number} total - returns the total number of words at the string
 * @example countStringWords(Model.title)
 */
function countStringWords(text) {
    let count = 0;
    const words = text.toString().split(' ');
    for (let i = 0, { length } = words; i < length; i++) {
        if (words[i] !== '') {
            count++;
        }
    }
    return count;
}
